import { useEffect, useRef, useState } from "react";
import { useBlocker } from "react-router-dom";
import { Check } from "lucide-react";
import { FEATURE_KEYS } from "@school-sales-ai/shared";
import { useCatalogQuery } from "@/hooks/useCatalog";
import { useClientFeatures } from "@/hooks/useClientFeatures";
import { CatalogForm } from "@/components/catalog/CatalogForm";
import {
  useCatalogEditor,
  type CatalogEditorSummary,
  type UnsavedRow,
} from "@/components/catalog/useCatalogEditor";
import { PageHeader } from "@/components/PageHeader";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

const SUBTITLE = "El agente usa este catálogo para calificar y ofrecer. Cada cambio se guarda solo.";

/** The page's one-line answer to "did my changes make it?". */
function SaveStatus({ summary }: { summary: CatalogEditorSummary }): React.JSX.Element {
  if (summary.saving > 0) {
    return <span className="text-[12px] text-ink-500">Guardando…</span>;
  }
  if (summary.errors > 0) {
    return (
      <span role="alert" className="text-[12px] font-medium text-red-600">
        {summary.errors === 1
          ? "1 cambio no se guardó"
          : `${summary.errors} cambios no se guardaron`}
      </span>
    );
  }
  if (summary.unsaved > 0) {
    return <span className="text-[12px] text-ink-500">Cambios pendientes…</span>;
  }
  return (
    <span className="flex items-center gap-1 text-[12px] text-ink-500">
      <Check size={13} className="text-moss" /> Todo guardado
    </span>
  );
}

/**
 * The catalog editor as its own section. It used to be a tab of Agente; it
 * moved out because the catalog is what the whole CRM reads (Pedidos,
 * Promociones, the product filters), not only the agent's configuration.
 *
 * Every edit saves itself, so leaving the page never asks "¿salir sin
 * guardar?" first: it saves what is pending and goes. It stops only when
 * something could not be saved, and names it.
 */
export function Catalogo(): React.JSX.Element {
  const { data: catalog } = useCatalogQuery();
  const editor = useCatalogEditor(catalog);

  // Decides which catalog shape the admin edits: the school's cycles + schedules,
  // or one flat product list.
  const { data: features } = useClientFeatures();
  const enrollmentEnabled = (features ?? []).includes(FEATURE_KEYS.enrollment);
  const ordersEnabled = (features ?? []).includes(FEATURE_KEYS.orders);
  const quotesEnabled = (features ?? []).includes(FEATURE_KEYS.quotes);

  const blocker = useBlocker(
    ({ currentLocation, nextLocation }) =>
      editor.summary.unsaved > 0 && currentLocation.pathname !== nextLocation.pathname,
  );
  const [unsaved, setUnsaved] = useState<UnsavedRow[] | null>(null);
  const flushing = useRef(false);
  const { flushAll } = editor;

  // Blocked navigation = save first, then go. Only a failure stays on the page.
  useEffect(() => {
    if (blocker.state !== "blocked" || flushing.current) return;
    flushing.current = true;
    void (async () => {
      const left = await flushAll();
      flushing.current = false;
      if (left.length === 0) {
        blocker.proceed();
        return;
      }
      setUnsaved(left);
    })();
  }, [blocker, flushAll]);

  function stay(): void {
    setUnsaved(null);
    if (blocker.state === "blocked") blocker.reset();
  }
  function leaveAnyway(): void {
    setUnsaved(null);
    if (blocker.state === "blocked") blocker.proceed();
  }

  return (
    <>
      <Dialog open={unsaved !== null} onOpenChange={(open) => !open && stay()}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {unsaved?.length === 1
                ? "1 cambio no se pudo guardar"
                : `${unsaved?.length ?? 0} cambios no se pudieron guardar`}
            </DialogTitle>
            <DialogDescription>
              Revisa los campos marcados. Si sales ahora, estos cambios se pierden.
            </DialogDescription>
          </DialogHeader>
          <ul className="list-disc pl-5 text-[13px] text-ink-700">
            {unsaved?.map((row) => (
              <li key={row.key}>{row.label}</li>
            ))}
          </ul>
          <DialogFooter>
            <button
              type="button"
              onClick={stay}
              className="rounded-lg bg-lime px-4 py-2 text-sm font-semibold text-moss hover:bg-lime-dk"
            >
              Quedarme
            </button>
            <button
              type="button"
              onClick={leaveAnyway}
              className="rounded-lg border border-ink-200 px-4 py-2 text-sm font-medium text-ink-700 hover:bg-ink-050"
            >
              Salir sin ellos
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <div className="flex h-full flex-col">
        <div className="md:hidden">
          <PageHeader
            title="Catálogo"
            subtitle={SUBTITLE}
            right={<SaveStatus summary={editor.summary} />}
          />
        </div>
        <div className="hidden items-center justify-between gap-4 border-b border-ink-100 px-6 py-4 md:flex">
          <div>
            <h1 className="font-display text-2xl font-bold text-ink-900">Catálogo</h1>
            <p className="mt-0.5 text-xs text-ink-400">{SUBTITLE}</p>
          </div>
          <SaveStatus summary={editor.summary} />
        </div>

        <div className="flex-1 overflow-y-auto px-4 py-4 md:px-6 md:py-5">
          <CatalogForm
            mode="maintenance"
            editor={editor}
            enrollmentEnabled={enrollmentEnabled}
            ordersEnabled={ordersEnabled}
            quotesEnabled={quotesEnabled}
            inactiveProducts={catalog?.inactiveProducts}
          />
        </div>
      </div>
    </>
  );
}
